import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { NewsLayout } from '@/layouts/NewsLayout';
import { ArticleCard } from '@/components/news/ArticleCard';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { Article, Tag } from '@/lib/types';
import { ArrowLeft, Hash } from 'lucide-react';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { useState } from 'react';

const ARTICLES_PER_PAGE = 12;

function transformArticle(data: any): Article {
  return {
    ...data,
    is_featured: data.is_featured ?? false,
    is_editors_pick: data.is_editors_pick ?? false,
    is_sponsored: data.is_sponsored ?? false,
    view_count: data.view_count ?? 0,
    share_count: data.share_count ?? 0,
    trending_score: data.trending_score ?? 0,
    reading_time: data.reading_time ?? 5,
    category: data.categories || null,
    author: data.profiles || null,
  };
}

export default function TagPage() {
  const { slug } = useParams<{ slug: string }>();
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch tag
  const { data: tag, isLoading: loadingTag } = useQuery({
    queryKey: ['tag', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tags')
        .select('*')
        .eq('slug', slug)
        .single();
      
      if (error) throw error;
      return data as Tag;
    },
    enabled: !!slug,
  });

  // Fetch articles for tag
  const { data: articles = [], isLoading: loadingArticles } = useQuery({
    queryKey: ['tag-articles', tag?.id],
    queryFn: async () => {
      // First get article IDs from article_tags
      const { data: taggedArticles, error: tagError } = await supabase
        .from('article_tags')
        .select('article_id')
        .eq('tag_id', tag!.id);
      
      if (tagError) throw tagError;
      if (!taggedArticles.length) return [];

      const articleIds = taggedArticles.map(ta => ta.article_id);

      const { data, error } = await supabase
        .from('articles')
        .select(`
          *,
          categories (*),
          profiles:author_id (*)
        `)
        .eq('status', 'published')
        .in('id', articleIds)
        .lte('publish_date', new Date().toISOString())
        .order('publish_date', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      return data.map(transformArticle);
    },
    enabled: !!tag?.id,
  });

  const isLoading = loadingTag || loadingArticles;
  const totalPages = Math.ceil(articles.length / ARTICLES_PER_PAGE);
  const startIndex = (currentPage - 1) * ARTICLES_PER_PAGE;
  const paginatedArticles = articles.slice(startIndex, startIndex + ARTICLES_PER_PAGE);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!loadingTag && !tag) {
    return (
      <NewsLayout>
        <div className="container py-24 text-center">
          <h1 className="text-2xl font-serif font-bold mb-4">Oznaka ni najdena</h1>
          <p className="text-muted-foreground mb-8">Oznaka, ki jo iščete, ne obstaja.</p>
          <Link to="/">
            <Button>
              <ArrowLeft className="mr-2 h-4 w-4" /> Nazaj na domačo stran
            </Button>
          </Link>
        </div>
      </NewsLayout>
    );
  }

  return (
    <NewsLayout>
      <div className="container py-12">
        {/* Back Link */}
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Nazaj na domačo stran
        </Link>

        {/* Header */}
        {loadingTag ? (
          <div className="mb-8">
            <Skeleton className="h-10 w-48 mb-2" />
          </div>
        ) : tag && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-2">
              <Hash className="w-8 h-8 text-primary" />
              <h1 className="font-serif text-3xl md:text-4xl font-bold">{tag.name}</h1>
            </div>
            <p className="text-muted-foreground">Članki z oznako #{tag.name}</p>
          </div>
        )}

        {/* Articles Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(12)].map((_, i) => (
              <Skeleton key={i} className="aspect-[4/3] rounded-xl" />
            ))}
          </div>
        ) : paginatedArticles.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground mb-4">Ni člankov s to oznako.</p>
            <Link to="/">
              <Button variant="outline">Prebrskajte vse članke</Button>
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {paginatedArticles.map((article, index) => (
                <ArticleCard key={article.id} article={article} index={index} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                      className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                  
                  {[...Array(totalPages)].map((_, i) => (
                    <PaginationItem key={i + 1}>
                      <PaginationLink
                        onClick={() => handlePageChange(i + 1)}
                        isActive={currentPage === i + 1}
                        className="cursor-pointer"
                      >
                        {i + 1}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                  
                  <PaginationItem>
                    <PaginationNext
                      onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                      className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </>
        )}
      </div>
    </NewsLayout>
  );
}
